/**
 * features/pos/store/cartStore.ts
 * Estado del carrito de compra con Zustand.
 *
 * ──────────────────────────────────────────────────────────────────
 * CAMBIO v3 — PRECIOS, DESCUENTOS, CANTIDAD
 * ──────────────────────────────────────────────────────────────────
 *  NUEVOS CAMPOS en CartItem:
 *    - originalPrice   → precio de catálogo (nunca se modifica)
 *    - priceNote       → motivo obligatorio si se cambió el precio
 *
 *  NUEVAS ACCIONES:
 *    - updatePrice(cartItemId, newPrice, note)
 *        Cambia unitPrice de una línea. Guarda originalPrice y note.
 *    - updateDiscount(cartItemId, discount, type)
 *        type: 'amount' | 'percent'
 *        Convierte % a $ antes de guardar (discount sigue siendo $).
 *    - setGlobalDiscount(value, type)
 *        Descuento global de la venta (% o $).
 *        Se aplica proporcional a cada línea al calcular totales.
 *    - clearGlobalDiscount()
 *
 *  NUEVO CAMPO en CartState:
 *    - globalDiscount      → valor numérico
 *    - globalDiscountType  → 'amount' | 'percent'
 *    - globalDiscountValue → descuento global en $ calculado
 *
 *  Sin cambios en: addItem (genéricos), updateQty, removeItem,
 *  setClient, clearCart, calcTotals (sigue siendo fuente de verdad).
 * ──────────────────────────────────────────────────────────────────
 */

import { create } from 'zustand'

// ── Función pura de resolución de precio por tier ─────────────────────────────
//
// Regla oficial:
//   vip  → priceVip ?? pricePro ?? priceRetail
//   pro  → pricePro ?? priceRetail
//   final / null / undefined → priceRetail
//
// Se exporta para que ProductGrid y otros módulos la reutilicen sin duplicar
// la lógica — única fuente de verdad del motor de precios.

export function resolveCartItemPriceByClientLevel(
  item: Pick<CartItem, 'priceRetail' | 'pricePro' | 'priceVip' | 'unitPrice'>,
  clientLevel: 'final' | 'pro' | 'vip' | null | undefined
): number {
  const level = clientLevel === 'pro' || clientLevel === 'vip' ? clientLevel : 'final'
  if (level === 'vip' && item.priceVip != null) return item.priceVip
  if ((level === 'vip' || level === 'pro') && item.pricePro != null) return item.pricePro
  return item.priceRetail ?? item.unitPrice
}

// % de BENEFICIO máximo del carrito, igual que pos_confirm_sale (00399): por línea, (catálogo del nivel
// del cliente − (precio cobrado − descuento)) / catálogo; precio rebajado y descuento se suman. El
// catálogo sigue la regla del servidor (price_c/price_b sólo si > 0, si no menudeo); una línea sin
// precios de catálogo cae a su precio original. Es lo que se muestra y se pide al autorizador.
export function beneficioMaximoPct(
  items: Pick<
    CartItem,
    'priceRetail' | 'pricePro' | 'priceVip' | 'unitPrice' | 'originalPrice' | 'discount'
  >[],
  clientLevel: 'final' | 'pro' | 'vip' | null | undefined
): number {
  return Math.max(
    0,
    ...items.map(i => {
      let catalogo = i.priceRetail ?? (i.originalPrice || i.unitPrice)
      if (clientLevel === 'vip' && (i.priceVip ?? 0) > 0) catalogo = i.priceVip as number
      else if ((clientLevel === 'vip' || clientLevel === 'pro') && (i.pricePro ?? 0) > 0)
        catalogo = i.pricePro as number
      return catalogo > 0 ? ((catalogo - (i.unitPrice - (i.discount ?? 0))) / catalogo) * 100 : 0
    })
  )
}

/** % que se pide al autorizador: el beneficio redondeado hacia arriba a 2 decimales para que el token
 *  cubra lo que el servidor recalcula (round a 4). */
export function porcentajeParaAutorizar(
  items: Parameters<typeof beneficioMaximoPct>[0],
  clientLevel: Parameters<typeof beneficioMaximoPct>[1]
): number {
  return Math.ceil(beneficioMaximoPct(items, clientLevel) * 100) / 100
}

export interface CartItem {
  cartItemId: string
  productId: string | null
  name: string
  code: string | null
  unitPrice: number // Precio actual (puede haber sido modificado)
  originalPrice: number // Precio original de catálogo — NUEVO
  priceNote: string // Motivo de cambio de precio — NUEVO
  unitCost: number
  qty: number
  discount: number // Descuento en $ por unidad
  taxRate: number
  taxIncluded: boolean
  total: number // (unitPrice - discount) * qty
  isGeneric?: boolean
  genericCategoryId?: string | null
  // Tiers de precio para recalcular al cambiar cliente (solo productos de catálogo)
  priceRetail?: number
  pricePro?: number | null
  priceVip?: number | null
  fromQuote?: boolean // true when imported from a cotización
  // Identidad por unidad. Un producto SERIAL/IMEI/VEHICLE no se puede cobrar hasta elegir
  // exactamente qty piezas; el servidor lo vuelve a exigir. NONE/LOT dejan esto en 'NONE'/vacío.
  trackingMode?: 'NONE' | 'LOT' | 'SERIAL' | 'IMEI' | 'VEHICLE'
  unitIds?: string[]
}

/** Un producto que exige elegir la pieza exacta antes de cobrarla. */
export function requiresUnitSelection(i: Pick<CartItem, 'trackingMode'>): boolean {
  return i.trackingMode === 'SERIAL' || i.trackingMode === 'IMEI' || i.trackingMode === 'VEHICLE'
}

/** Cuándo una línea rastreada ya tiene sus piezas: tantas como cantidad, ni una menos. */
export function unitSelectionComplete(
  i: Pick<CartItem, 'trackingMode' | 'qty' | 'unitIds'>
): boolean {
  if (!requiresUnitSelection(i)) return true
  return (i.unitIds?.length ?? 0) === Math.round(i.qty)
}

export interface CartState {
  cartId: string
  items: CartItem[]
  /** Línea agregada/incrementada más recientemente — para resaltarla brevemente (feedback). */
  lastTouchedId: string | null
  /** Contador que sube en cada add, para re-disparar el resalte aunque sea la misma línea. */
  lastTouchedSeq: number
  clientId: string | null
  clientName: string | null
  clientBusinessName: string | null
  clientLevel: 'final' | 'pro' | 'vip'

  // Descuento global
  globalDiscount: number // valor ingresado (% o $)
  globalDiscountType: 'amount' | 'percent'
  globalDiscountValue: number // descuento global en $ calculado

  // Totales
  subtotal: number
  totalDiscount: number // descuentos por línea + global
  tax: number
  total: number

  // Quote import metadata — vacío hasta que se importe una cotización
  quoteId: string | null
  quoteFolio: string | null
  quoteOriginStoreId: string | null
  quoteOriginStoreCode: string | null
  quoteOriginBusinessId: string | null
  setQuoteImport: (meta: {
    quoteId: string
    quoteFolio: string
    originStoreId: string
    originStoreCode: string
    originBusinessId: string
  }) => void
  clearQuoteImport: () => void
  removeQuoteItems: () => void

  // Acciones base
  addItem: (
    item: Omit<CartItem, 'total' | 'cartItemId' | 'originalPrice' | 'priceNote' | 'taxIncluded'> & {
      cartItemId?: string
      taxIncluded?: boolean
    }
  ) => void
  updateQty: (cartItemId: string, qty: number) => void
  removeItem: (cartItemId: string) => void

  // Precio y descuento por línea
  updatePrice: (cartItemId: string, newPrice: number, note: string) => void
  updateDiscount: (cartItemId: string, discount: number, type?: 'amount' | 'percent') => void

  // Identidad por unidad de una línea rastreada.
  setUnitIds: (cartItemId: string, unitIds: string[]) => void

  // Descuento global
  setGlobalDiscount: (value: number, type: 'amount' | 'percent') => void
  clearGlobalDiscount: () => void

  setClient: (
    id: string | null,
    name: string | null,
    level?: 'final' | 'pro' | 'vip',
    businessName?: string | null
  ) => void
  clearCart: () => void

  /** Editores de precio ABIERTOS sin confirmar (un token por fila visible). Mientras haya alguno
   *  no se cobra: el número escrito todavía no es el precio del carrito. No se persiste. */
  priceEditing: string[]
  setPriceEditing: (token: string, on: boolean) => void

  // Configuración fiscal de tienda (sincronizado desde useAuth al iniciar POS)
  taxEnabled: boolean
  setTaxEnabled: (enabled: boolean) => void
}

// ── Descuento a la venta completa → por pieza ─────────────────────────────

/**
 * Reparte el descuento a la venta completa en cada línea, en centavos enteros POR PIEZA, porque el
 * servidor (pos_confirm_sale) sólo conoce el descuento por pieza de cada producto. Devuelve las líneas
 * con `discount` = el de la línea + su parte; sin descuento global devuelve el mismo arreglo.
 * Lo usan los totales del carrito y lo que se envía al cobrar: lo que se ve es lo que se cobra.
 * % → a cada pieza su % (hacia abajo, nunca más del pedido). $ → proporcional al neto de cada línea y
 * los centavos que sobran a las líneas más grandes; si la cantidad no deja repartir el último centavo,
 * el ahorro real (que es el que se muestra) queda unos centavos por debajo de lo tecleado.
 */
export function conDescuentoGlobal(
  items: CartItem[],
  valor: number,
  tipo: 'amount' | 'percent'
): CartItem[] {
  if (!(valor > 0) || items.length === 0) return items
  const neto = items.map(i => Math.max(0, Math.round((i.unitPrice - i.discount) * 100)))
  let extra: number[]
  if (tipo === 'percent') {
    const pct = Math.min(valor, 100)
    extra = neto.map(n => Math.floor((n * pct) / 100))
  } else {
    const base = items.reduce((s, i, k) => s + neto[k]! * i.qty, 0)
    const pedido = Math.min(Math.round(valor * 100), base)
    if (base <= 0) return items
    extra = items.map((i, k) => Math.floor((pedido * neto[k]!) / base))
    let resto = pedido - items.reduce((s, i, k) => s + extra[k]! * i.qty, 0)
    const orden = items
      .map((_, k) => k)
      .sort((a, b) => neto[b]! * items[b]!.qty - neto[a]! * items[a]!.qty)
    for (const k of orden) {
      const mas = Math.min(Math.floor(resto / items[k]!.qty), neto[k]! - extra[k]!)
      if (mas > 0) {
        extra[k]! += mas
        resto -= mas * items[k]!.qty
      }
    }
  }
  return items.map((i, k) =>
    extra[k] ? calcItemTotal({ ...i, discount: Math.round(i.discount * 100 + extra[k]!) / 100 }) : i
  )
}

// ── Cálculo de totales ────────────────────────────────────────────────────

function calcTotals(
  cartItems: CartItem[],
  globalDiscount: number,
  globalDiscountType: 'amount' | 'percent',
  taxEnabled = true
) {
  // Descuentos calculados sobre precio bruto para preservar UX del cajero
  // (un descuento de $10 en un producto de $80 sigue siendo $10 menos para el cliente).
  // El descuento global ya va repartido por pieza en `items`, igual que lo cobra el servidor.
  const items = conDescuentoGlobal(cartItems, globalDiscount, globalDiscountType)
  const grossLineDisc = cartItems.reduce((s, i) => s + i.discount * i.qty, 0)
  const allDisc = items.reduce((s, i) => s + i.discount * i.qty, 0)
  const totalDiscount = r2(allDisc)
  const globalDiscountValue = r2(allDisc - grossLineDisc)

  // Línea por línea y redondeando cada una, como pos_confirm_sale: neto = round((precio − desc) × cant);
  // IVA incluido = round(neto − neto/(1+tasa)); IVA agregado = round(neto × tasa). Tienda sin IVA ⇒ tasa 0.
  // Sumar sin redondear por línea dejaba centavos de diferencia y el cobro exacto fallaba.
  let subtotal = 0
  let tax = 0
  let total = 0
  for (const i of items) {
    const neto = r2((i.unitPrice - i.discount) * i.qty)
    const tasa = taxEnabled ? i.taxRate : 0
    const iva = i.taxIncluded ? r2(neto - neto / (1 + tasa / 100)) : r2((neto * tasa) / 100)
    subtotal += i.taxIncluded ? neto - iva : neto
    tax += iva
    total += i.taxIncluded ? neto : neto + iva
  }

  return {
    subtotal: r2(subtotal),
    totalDiscount,
    globalDiscountValue,
    tax: r2(tax),
    total: r2(total),
  }
}

/** Redondeo a centavos (mitad hacia arriba, como round(x, 2) de Postgres para importes positivos). */
function r2(x: number): number {
  return Math.round((x + Number.EPSILON) * 100) / 100
}

function calcItemTotal(item: Omit<CartItem, 'total'>): CartItem {
  return {
    ...item,
    total: Math.round((item.unitPrice - item.discount) * item.qty * 100) / 100,
  }
}

// ── Store ─────────────────────────────────────────────────────────────────

export const useCartStore = create<CartState>((set, get) => ({
  priceEditing: [],
  setPriceEditing: (token, on) =>
    set(s => {
      const has = s.priceEditing.includes(token)
      if (on === has) return s
      return {
        priceEditing: on ? [...s.priceEditing, token] : s.priceEditing.filter(t => t !== token),
      }
    }),
  cartId: crypto.randomUUID(),
  items: [],
  lastTouchedId: null,
  lastTouchedSeq: 0,
  clientId: null,
  clientName: null,
  clientBusinessName: null,
  clientLevel: 'final',
  globalDiscount: 0,
  globalDiscountType: 'amount',
  globalDiscountValue: 0,
  subtotal: 0,
  totalDiscount: 0,
  tax: 0,
  total: 0,
  taxEnabled: true,
  quoteId: null,
  quoteFolio: null,
  quoteOriginStoreId: null,
  quoteOriginStoreCode: null,
  quoteOriginBusinessId: null,

  // ── addItem ────────────────────────────────────────────────────────────
  addItem: item => {
    const { items, globalDiscount, globalDiscountType } = get()
    const isGeneric = item.isGeneric === true || !item.productId
    let newItems: CartItem[]
    let touchedId: string // línea agregada o incrementada, para el resalte visual

    const base = {
      ...item,
      originalPrice: item.unitPrice, // guardar precio original
      priceNote: '',
      discount: item.discount ?? 0,
      taxIncluded: item.taxIncluded ?? true,
    }

    if (!isGeneric && item.productId) {
      const existing = items.find(i => !i.isGeneric && i.productId === item.productId)
      if (existing) {
        touchedId = existing.cartItemId
        newItems = items.map(i =>
          i.cartItemId === existing.cartItemId ? calcItemTotal({ ...i, qty: i.qty + item.qty }) : i
        )
      } else {
        touchedId = item.cartItemId || crypto.randomUUID()
        newItems = [...items, calcItemTotal({ ...base, cartItemId: touchedId, isGeneric: false })]
      }
    } else {
      touchedId = item.cartItemId || crypto.randomUUID()
      newItems = [
        ...items,
        calcItemTotal({
          ...base,
          cartItemId: touchedId,
          productId: null,
          isGeneric: true,
          genericCategoryId: item.genericCategoryId ?? null,
        }),
      ]
    }

    set({
      items: newItems,
      lastTouchedId: touchedId,
      lastTouchedSeq: get().lastTouchedSeq + 1,
      ...calcTotals(newItems, globalDiscount, globalDiscountType, get().taxEnabled),
    })
  },

  // ── updateQty ──────────────────────────────────────────────────────────
  updateQty: (cartItemId, qty) => {
    if (qty <= 0) {
      get().removeItem(cartItemId)
      return
    }
    const { items, globalDiscount, globalDiscountType, taxEnabled } = get()
    const newItems = items.map(i => {
      if (i.cartItemId !== cartItemId) return i
      // Si baja la cantidad por debajo de las piezas ya elegidas, sobran identidades: se recortan
      // las ultimas para que nunca haya mas unidades que cantidad.
      const unitIds =
        i.unitIds && i.unitIds.length > Math.round(qty)
          ? i.unitIds.slice(0, Math.round(qty))
          : i.unitIds
      return calcItemTotal({ ...i, qty, unitIds })
    })
    set({
      items: newItems,
      ...calcTotals(newItems, globalDiscount, globalDiscountType, taxEnabled),
    })
  },

  // ── removeItem ─────────────────────────────────────────────────────────
  removeItem: cartItemId => {
    const { globalDiscount, globalDiscountType, taxEnabled } = get()
    const newItems = get().items.filter(i => i.cartItemId !== cartItemId)
    set({
      items: newItems,
      ...calcTotals(newItems, globalDiscount, globalDiscountType, taxEnabled),
    })
  },

  // ── updatePrice ────────────────────────────────────────────────────────
  // Cambia el precio unitario de una línea. Requiere motivo (note).
  // originalPrice se guarda solo la primera vez.
  updatePrice: (cartItemId, newPrice, note) => {
    const { items, globalDiscount, globalDiscountType, taxEnabled } = get()
    const newItems = items.map(i => {
      if (i.cartItemId !== cartItemId) return i
      return calcItemTotal({
        ...i,
        unitPrice: Math.max(0, newPrice),
        originalPrice: i.originalPrice || i.unitPrice, // no pisar si ya existe
        priceNote: note,
        // resetear descuento si el precio nuevo es menor al descuento
        discount: Math.min(i.discount, Math.max(0, newPrice)),
      })
    })
    set({
      items: newItems,
      ...calcTotals(newItems, globalDiscount, globalDiscountType, taxEnabled),
    })
  },

  // ── updateDiscount ─────────────────────────────────────────────────────
  // type: 'amount' ($ directo) | 'percent' (% del precio unitario)
  updateDiscount: (cartItemId, discount, type = 'amount') => {
    const { items, globalDiscount, globalDiscountType, taxEnabled } = get()
    const newItems = items.map(i => {
      if (i.cartItemId !== cartItemId) return i
      const discountInPesos =
        type === 'percent' ? Math.round(i.unitPrice * (discount / 100) * 100) / 100 : discount
      return calcItemTotal({
        ...i,
        discount: Math.max(0, Math.min(discountInPesos, i.unitPrice)),
      })
    })
    set({
      items: newItems,
      ...calcTotals(newItems, globalDiscount, globalDiscountType, taxEnabled),
    })
  },

  // ── setUnitIds ─────────────────────────────────────────────────────────
  // Las piezas exactas de una linea rastreada. No toca precio ni total: identificar no es cobrar.
  setUnitIds: (cartItemId, unitIds) => {
    set({
      items: get().items.map(i =>
        i.cartItemId === cartItemId ? { ...i, unitIds: [...unitIds] } : i
      ),
    })
  },

  // ── setGlobalDiscount ──────────────────────────────────────────────────
  setGlobalDiscount: (value, type) => {
    const { items, taxEnabled } = get()
    const gd = Math.max(0, value)
    const totals = calcTotals(items, gd, type, taxEnabled)
    set({ globalDiscount: gd, globalDiscountType: type, ...totals })
  },

  // ── clearGlobalDiscount ────────────────────────────────────────────────
  clearGlobalDiscount: () => {
    const { items, taxEnabled } = get()
    set({
      globalDiscount: 0,
      globalDiscountType: 'amount',
      ...calcTotals(items, 0, 'amount', taxEnabled),
    })
  },

  // ── setClient ──────────────────────────────────────────────────────────
  setClient: (id, name, level = 'final', businessName = null) => {
    const { items, globalDiscount, globalDiscountType, taxEnabled } = get()
    // Normalizar: cualquier valor fuera de 'pro'|'vip' cae a 'final'
    const effectiveLevel: 'final' | 'pro' | 'vip' =
      level === 'pro' || level === 'vip' ? level : 'final'

    const newItems = items.map(i => {
      // No recalcular: genéricos, legacy sin priceRetail, precio manual activo
      if (i.isGeneric || i.priceRetail === undefined || i.priceNote !== '') return i
      const newPrice = resolveCartItemPriceByClientLevel(i, effectiveLevel)
      if (newPrice === i.unitPrice) return i
      return calcItemTotal({
        ...i,
        unitPrice: newPrice,
        // Actualizar originalPrice al nuevo precio de tier para que el badge
        // "Precio mod." solo aparezca en cambios manuales (priceNote !== '')
        originalPrice: newPrice,
        discount: Math.min(i.discount, newPrice),
      })
    })

    set({
      clientId: id,
      clientName: name,
      clientLevel: effectiveLevel,
      clientBusinessName: businessName,
      items: newItems,
      ...calcTotals(newItems, globalDiscount, globalDiscountType, taxEnabled),
    })
  },

  // ── clearCart ──────────────────────────────────────────────────────────
  // taxEnabled se preserva — depende de config de tienda, no del carrito
  clearCart: () =>
    set(s => ({
      cartId: crypto.randomUUID(),
      items: [],
      clientId: null,
      clientName: null,
      clientBusinessName: null,
      clientLevel: 'final',
      globalDiscount: 0,
      globalDiscountType: 'amount',
      globalDiscountValue: 0,
      subtotal: 0,
      totalDiscount: 0,
      tax: 0,
      total: 0,
      taxEnabled: s.taxEnabled,
      quoteId: null,
      quoteFolio: null,
      quoteOriginStoreId: null,
      quoteOriginStoreCode: null,
      quoteOriginBusinessId: null,
    })),

  // ── setQuoteImport / clearQuoteImport / removeQuoteItems ─────────────────
  setQuoteImport: ({ quoteId, quoteFolio, originStoreId, originStoreCode, originBusinessId }) =>
    set({
      quoteId,
      quoteFolio,
      quoteOriginStoreId: originStoreId,
      quoteOriginStoreCode: originStoreCode,
      quoteOriginBusinessId: originBusinessId,
    }),

  clearQuoteImport: () =>
    set({
      quoteId: null,
      quoteFolio: null,
      quoteOriginStoreId: null,
      quoteOriginStoreCode: null,
      quoteOriginBusinessId: null,
    }),

  removeQuoteItems: () => {
    const { items, globalDiscount, globalDiscountType, taxEnabled } = get()
    const remaining = items.filter(i => !i.fromQuote)
    set({
      items: remaining,
      ...calcTotals(remaining, globalDiscount, globalDiscountType, taxEnabled),
      quoteId: null,
      quoteFolio: null,
      quoteOriginStoreId: null,
      quoteOriginStoreCode: null,
      quoteOriginBusinessId: null,
    })
  },

  // ── setTaxEnabled ──────────────────────────────────────────────────────
  // Sincronizado por PosContent desde storeConfig al montar el POS
  setTaxEnabled: (enabled: boolean) => {
    const { items, globalDiscount, globalDiscountType } = get()
    set({
      taxEnabled: enabled,
      ...calcTotals(items, globalDiscount, globalDiscountType, enabled),
    })
  },
}))
